import { Capacitor } from '@capacitor/core';
import { Browser } from '@capacitor/browser';
import { Share } from '@capacitor/share';

export function createPageUrl(path) {
    return `/${path}`;
}

export function cn(...inputs) {
    // Simple fallback if clsx/tailwind-merge not available in this scope, 
    // but usually we import them. For now just joining.
    return inputs.filter(Boolean).join(" ");
}

/**
 * Retourne l'URL de base du site distant uniquement en environnement mobile Capacitor
 * (ou si REACT_APP_SITE_URL est défini). Sur le Web, retourne '' pour conserver les URLs relatives.
 */
export function getSiteUrl() {
    if (process.env.REACT_APP_SITE_URL) {
        return process.env.REACT_APP_SITE_URL;
    }
    try {
        if (Capacitor.isNativePlatform()) {
            return 'https://pepitespot.fr';
        }
    } catch (e) {
        // ignore
    }
    return '';
}

/**
 * Helper centralisé pour résoudre les images distantes ou servies par le backend (/logos, /api/assets, /api/images/proxy)
 * Utilise https://pepitespot.fr uniquement en environnement mobile natif.
 */
export function getRemoteImageUrl(url) {
    if (!url) return '';
    if (url.startsWith('data:') || url.startsWith('http://') || url.startsWith('https://')) {
        return url;
    }
    const siteUrl = getSiteUrl();
    if (!siteUrl) return url;

    if (url.startsWith('/logos/') || url.startsWith('/api/')) {
        return `${siteUrl}${url}`;
    }
    return url;
}

// Helper to detect Shopify URLs
const isShopifyUrl = (url) => {
    try {
        const hostname = new URL(url).hostname;
        return hostname.includes('cdn.shopify.com');
    } catch (e) {
        return false;
    }
};

// Helper to construct Optimized Shopify URL
const getShopifyOptimizedUrl = (url, width, height, quality = 90) => {
    try {
        const urlObj = new URL(url);

        // Use searchParams to safely update/append parameters
        if (width) urlObj.searchParams.set('width', width);
        if (height) urlObj.searchParams.set('height', height);

        // Add crop center only if both dimensions are present to preserve aspect ratio focus
        if (width && height) urlObj.searchParams.set('crop', 'center');

        urlObj.searchParams.set('format', 'webp');
        urlObj.searchParams.set('quality', quality);

        return urlObj.toString();
    } catch (e) {
        return url;
    }
};

export function getOptimizedImageUrl(url, width, quality = 90) {
    if (!url) return '';
    if (url.startsWith('data:')) return url;
    if (url.startsWith('/')) {
        return getRemoteImageUrl(url);
    }

    // Client-side detection for Retina
    const pixelRatio = typeof window !== 'undefined' && window.devicePixelRatio ? window.devicePixelRatio : 2;
    // Cap pixel ratio at 2 to avoid excessive sizes, but ensure crispness
    const effectiveRatio = Math.min(Math.max(pixelRatio, 1), 2);
    const targetWidth = width ? Math.round(width * effectiveRatio) : undefined;

    // Unified Logic: If Shopify URL, return direct optimized URL
    if (isShopifyUrl(url)) {
        return getShopifyOptimizedUrl(url, targetWidth, undefined, quality);
    }

    // Default Proxy Logic
    const params = new URLSearchParams({
        url: url,
        quality: quality.toString()
    });

    if (targetWidth) params.append('width', targetWidth.toString());

    const siteUrl = getSiteUrl();
    return `${siteUrl}/api/images/proxy?${params.toString()}`;
}

/**
 * Ouvre un lien marchand externe via Chrome Custom Tabs (@capacitor/browser) sur mobile natif,
 * ou via window.open classique sur le Web.
 */
export async function openExternalUrl(url, event) {
    if (event && typeof event.preventDefault === 'function') {
        event.preventDefault();
    }
    if (event && typeof event.stopPropagation === 'function') {
        event.stopPropagation();
    }
    if (!url) return;

    try {
        if (Capacitor.isNativePlatform()) {
            await Browser.open({ url });
            return;
        }
    } catch (err) {
        console.warn('[openExternalUrl] Browser.open failed, falling back to window.open:', err);
    }

    if (typeof window !== 'undefined') {
        window.open(url, '_blank', 'noopener,noreferrer');
    }
}

/**
 * Partage du contenu via le tiroir natif Android (@capacitor/share) sur mobile,
 * avec fallback navigator.share puis presse-papier sur le Web.
 */
export async function shareContent({ title, text, url, dialogTitle = 'Partager' }) {
    try {
        if (Capacitor.isNativePlatform()) {
            await Share.share({
                title,
                text,
                url,
                dialogTitle,
            });
            return { shared: true, method: 'native' };
        }
    } catch (err) {
        if (err && err.name !== 'AbortError' && !String(err.message || '').toLowerCase().includes('cancel')) {
            console.warn('[shareContent] Native share error:', err);
        } else {
            return { shared: false, cancelled: true };
        }
    }

    if (typeof navigator !== 'undefined' && navigator.share) {
        try {
            await navigator.share({ title, text, url });
            return { shared: true, method: 'web-share' };
        } catch (err) {
            if (err && err.name === 'AbortError') {
                return { shared: false, cancelled: true };
            }
        }
    }

    if (typeof navigator !== 'undefined' && navigator.clipboard && url) {
        await navigator.clipboard.writeText(url);
        return { shared: true, method: 'clipboard' };
    }

    return { shared: false };
}
